import React, { useState, useCallback, memo, useEffect } from "react";
import moment from "moment";
import { Carousel, OverlayTrigger, Tooltip } from "react-bootstrap";
import ReportingIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/From_Date.svg";
import congraPic from "../../../../assets/Congratulations.svg";
import awardCertiPic from "../../../../assets/Awards & Certifications.svg";
import styled from 'styled-components';
import ImageModalComponent from "../../../../modules/Dashboard/DashboardComponent/ImageModalComponent/ImageModalComponent";

const ProfileEmpCard = memo(({
  empData = {},
  employeeCanEdit,
  groupedAwards = [],
  onProfilePhotoChange,
}) => {
  const [index, setIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 992 && window.innerWidth > 768);
  const [isExtraLarge, setIsExtraLarge] = useState(window.innerWidth > 1600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 992 && window.innerWidth > 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSelect = useCallback((selectedIndex) => {
    setIndex(selectedIndex);
  }, []);

  const handleImageClick = useCallback((imageUrl) => {
    if (imageUrl) {
      setSelectedImage(imageUrl);
      setIsImageModalOpen(true);
    }
  }, []);

  const closeImageModal = useCallback(() => {
    setIsImageModalOpen(false);
    setSelectedImage("");
  }, []);
  const StyledAwardCard = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  text-align: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  padding: 6px 4px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  width: 45px;
  height: 55px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-bottom: 4px solid #CD9F52;

  @media (min-width: 1600px) {
    width: 60px;
    height: 75px;
    padding: 8px 6px;
  }
`;

const CardContent = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DateText = styled.div`
  color: #000;
  font-weight: bold;
  font-size: 9px;
  border-radius: 4px;
  letter-spacing: 0.3px;

  @media (min-width: 1600px) {
    font-size: 10px;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 20px;
  flex: 1;

  @media (min-width: 1600px) {
    padding-bottom: 25px;
  }
`;

const AwardImage = styled.img`
  height: 30px;
  width: auto;
  max-width: 45px;
  object-fit: contain;

  @media (min-width: 1600px) {
    height: 40px;
    max-width: 55px;
  }
`;

  const renderAwardItem = useCallback((award) => {
    const dateMatch = award.AwardDetails.match(/on ([A-Za-z]{3} \d{1,2})/);
    const formattedDate = dateMatch 
      ? dateMatch[1].replace(' ', '-') 
      : "";

    return (
      <div key={award.ID} style={{ flex: "0 0 auto", margin: "0 2px" }}>
     <StyledAwardCard>
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-${award.ID}`}>
            {award.AwardDetails}
          </Tooltip>
        }
      >
        <CardContent>
          <DateText>{formattedDate}</DateText>
          <ImageContainer>
            <AwardImage
              src={award.FileBage}
              alt={award.Name}
              className="card-img-top"
            />
          </ImageContainer>
        </CardContent>
      </OverlayTrigger>
    </StyledAwardCard>
      </div>
    );
  }, []);

  const renderAwards = () => {
    const flatAwards = groupedAwards.flat();
    const awardsCount = flatAwards.length;
    
    // Determine items per slide based on screen size
    const itemsPerSlide = isMobile ? 4 : (isTablet ? 6 : (isExtraLarge ? 10 : 8));

    if (awardsCount <= itemsPerSlide) {
      return (
        <div className="d-flex mt-2" style={{ 
          flexDirection: 'row-reverse', 
          gap: "8px",
          justifyContent: 'flex-start'
        }}>
          {flatAwards.map(renderAwardItem)}
        </div>
      );
    }

    // Split awards into chunks based on itemsPerSlide
    const awardChunks = [];
    for (let i = 0; i < awardsCount; i += itemsPerSlide) {
      awardChunks.push(flatAwards.slice(i, i + itemsPerSlide));
    }

    return (
      <Carousel
        activeIndex={index}
        onSelect={handleSelect}
        interval={null}
        indicators={false}
        controls={awardsCount > itemsPerSlide}
        prevIcon={
          <span 
            aria-hidden="true"
            style={{
              display: awardsCount > itemsPerSlide ? 'block' : 'none',
              width: "30px",
              height: "30px"
            }}
          >
            <svg 
              width="100%" 
              height="100%" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="black" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </span>
        }
        nextIcon={
          <span 
            aria-hidden="true"
            style={{
              display: awardsCount > itemsPerSlide ? 'block' : 'none',
              width: "30px",
              height: "30px"
            }}
          >
            <svg 
              width="100%" 
              height="100%" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="black" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </span>
        }
      >
        {awardChunks.map((chunk, idx) => (
          <Carousel.Item key={idx}>
            <div className="d-flex mt-2" style={{ 
              flexDirection: 'row-reverse', 
              gap: "8px",
              justifyContent: awardsCount > itemsPerSlide ? 'center' : 'flex-start'
            }}>
              {chunk.map(renderAwardItem)}
            </div>
          </Carousel.Item>
        ))}
      </Carousel>
    );
  };
useEffect(() => {
  const handleResize = () => {
    setIsExtraLarge(window.innerWidth > 1200);
  };
  
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

  return (
    <div className="emp-profile-section">
      {/* First Row - Profile and Contact Info */}
      <div className="row align-items-center">
        {/* Profile Image and Basic Info - col-xl-3 on desktop, col-md-6 on tablet, col-12 on mobile */}
        <div className="col-xl-3 col-md-6 col-12">
          <div className="d-flex align-items-center m-2">
            <div className="position-relative me-3">
              <img
                src={empData?.FilePath || "/default-profile.png"}
                alt="Profile"
                className="emp-profile-img rounded-circle"
                onClick={() => handleImageClick(empData?.FilePath)}
                style={{ cursor: empData?.FilePath ? "pointer" : "default" }}
              />
              {employeeCanEdit === 1 && (
                <label
                  htmlFor="profilePhotoUpload"
                  className="position-absolute bottom-0 end-0 text-white rounded-circle p-2"
                  style={{
                    width: "30px",
                    height: "30px",
                    cursor: "pointer",
                    transform: "translate(25%, 25%)",
                    border: "2px solid white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#27568B",
                  }}
                >
                  <i className="fa fa-camera fa-xs"></i>
                  <input
                    id="profilePhotoUpload"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onProfilePhotoChange}
                  />
                </label>
              )}
            </div>
            <div className="emp-profile-info">
              <h2 className="m-0">{empData?.ContactName || ""}</h2>
              <p className="m-0">{empData?.EmpID || ""}</p>
              <p className="m-0">{empData?.Designation || ""}</p>
              {empData?.SubBrand?.trim() && (
                <p className="m-0">Band: {empData.SubBrand}</p>
              )}
              {empData?.Department?.trim() && (
                <p className="m-0">
                  Dept: {empData.Department}
                  {empData?.SubDepartment?.trim() && ` (${empData.SubDepartment})`}
                </p>
              )}
              {empData?.DateOfBirth && (
                <div className="emp-profile-dob m-0">
                  <i className="fa fa-birthday-cake"></i>
                  {moment(empData.DateOfBirth).format("MMMM D")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information - col-xl-3 on desktop, col-md-6 on tablet, col-12 on mobile */}
        <div className="col-xl-3 col-md-6 col-12 emp-profile-contact">
          {empData?.ImmediateSuperior && (
            <div className="emp-contact-item">
              <div className="emp-contact-icon">
                <img
                  src={ReportingIcon}
                  alt="reporting"
                  height="18"
                  width="18"
                />
              </div>
              <div className="report">
                Reporting to {empData.ImmediateSuperior}
              </div>
            </div>
          )}

          {empData?.Email && (
            <div className="emp-contact-item">
              <div className="emp-contact-icon">
                <i
                  className="bi bi-envelope mt-1"
                  style={{ color: "#fff" }}
                ></i>
              </div>
              <div className="report">{empData.Email}</div>
            </div>
          )}

         {empData?.JoiningDate && (
            <div className="emp-contact-item">
              <div className="emp-contact-icon">
                <i className="bi bi-calendar3" style={{ color: "#fff" }}></i>
              </div>

              <div className="report">
                With Nice Since{" "}
                {(() => {
                  const joiningDate = moment(empData.JoiningDate);

                  // Use relieving date if present, else use today
                  const endDate = empData.RelivingDate
                    ? moment(empData.RelivingDate)
                    : moment();

                  const years = endDate.diff(joiningDate, "years");
                  joiningDate.add(years, "years");

                  const months = endDate.diff(joiningDate, "months");

                  return `${moment(empData.JoiningDate).format(
                    "DD MMM YYYY"
                  )} ( ${years}Y ${months}M )`;
                })()}
              </div>
            </div>
          )}
             {empData?.JoiningDate &&
  (!empData.RelivingDate) && (
    <div className="emp-contact-item">
      <div className="emp-contact-icon">
        <i className="fa fa-address-card-o" style={{ color: "#fff" }}></i>
      </div>

      <div className="report">
        {(() => {
          // ---- Parse backend TotalYearExp (if exists) ----
          let pastYears = 0;
          let pastMonths = 0;

          if (empData.TotalYearExp && empData.TotalYearExp.trim() !== "") {
            const match = empData.TotalYearExp.match(
              /(\d+)\s*Years?\s*(\d+)?\s*Months?/i
            );

            if (match) {
              pastYears = parseInt(match[1], 10);
              pastMonths = match[2] ? parseInt(match[2], 10) : 0;
            }
          }

          // ---- Calculate current experience (JoiningDate → Today) ----
          const joiningDate = moment(empData.JoiningDate);
          const endDate = moment();

          const currentYears = endDate.diff(joiningDate, "years");
          const currentMonths = endDate.diff(
            joiningDate.add(currentYears, "years"),
            "months"
          );

          // ---- Add past + current experience ----
          const totalMonths =
            (pastYears + currentYears) * 12 +
            (pastMonths + currentMonths);

          const totalYearsFinal = Math.floor(totalMonths / 12);
          const totalMonthsFinal = totalMonths % 12;

          return (
            <>
              Total Exp:{" "}
              {`${totalYearsFinal} Years ${totalMonthsFinal} Months`}
            </>
          );
        })()}
      </div>
    </div>
  )}
        </div>

        {/* Awards Section - col-xl-6 on desktop, col-12 on tablet/mobile */}
        <div className={`col-xl-6 col-12 emp-profile-award ${groupedAwards.length === 0 ? "award-banner" : ""
          }`}
        >
          <div className="d-flex justify-content-between">
            <div className="emp-congrat">
              <img src={awardCertiPic} alt="congrat" />
            </div>
            {groupedAwards.length > 0 && (
              <div className="emp-congrat" style={{ marginRight:"10px" }}>
                <img src={congraPic} alt="congrat" />
              </div>
            )}
          </div>
          {groupedAwards.length === 0 ? (
            <div className="flex items-center justify-center">
              <div className="text-center max-w-md award-thought" >
                <p
                  style={{
                    lineHeight: "1.1",
                    fontFamily: "'Prosto One', 'Arial', sans-serif",
                  }}
                >
                  <span style={{ color: "#27568B", fontSize: "20px" }}>
                    Keep learning
                  </span>
                  <span className="text-gray-600">, </span>
                  <span style={{ color: "#27568B", fontSize: "20px" }}>
                    keep evolving
                  </span>
                  <span className="text-gray-600">,</span>
                  <br />
                  <span style={{ color: "black", fontSize: "20px" }}>
                    your next{" "}
                  </span>
                  <span style={{ color: "#27568B", fontSize: "20px" }}>
                    milestone
                  </span>
                  <br />
                  <span style={{ color: "black", fontSize: "20px" }}>
                    is just{" "}
                  </span>
                  <span style={{ color: "#27568B", fontSize: "20px" }}>
                    ahead
                  </span>
                  <span className="text-gray-600">.</span>
                </p>
              </div>
            </div>
          ) : (
            renderAwards()
          )}
        </div>
      </div>
      <ImageModalComponent
        isOpen={isImageModalOpen}
        imageUrl={selectedImage}
        onClose={closeImageModal}
      />
    </div>
  );
});

export default ProfileEmpCard;